import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { KPICard } from "@/components/KPICard";
import { StatusBadge } from "@/components/StatusBadge";
import { MissingPriceBacklog } from "@/components/MissingPriceBacklog";
import { usd, pct, ddmmmyy, statusTone, plTone, plLabel } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, Line, ComposedChart, Legend,
} from "recharts";

const Dashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [shipments, setShipments] = useState({ rows: [], total: 0 });
  const [filters, setFilters] = useState({ customer: "", status: "" });
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    const params = {};
    if (filters.customer) params.customer = filters.customer;
    if (filters.status) params.status = filters.status;
    const [sum, ships] = await Promise.all([
      apiClient.get("/dashboard/summary", { params }),
      apiClient.get("/shipments", { params: { ...params, page: 1, page_size: 20 } }),
    ]);
    setSummary(sum.data);
    setShipments(ships.data);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [filters]);

  const customerOptions = useMemo(() => {
    if (!summary?.by_customer) return [];
    return summary.by_customer.map((c) => c.customer);
  }, [summary]);

  const plT = plTone(summary?.net_pl);
  const plL = plLabel(summary?.net_pl);

  return (
    <div>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Overview</div>
        <h1 className="text-2xl font-bold text-[#0F2044]">Reconciliation dashboard</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-md p-4 mb-6 flex flex-wrap gap-3 sticky top-0 z-10" style={{ border: "1px solid var(--color-border)" }} data-testid="dashboard-filters">
        <select
          data-testid="filter-customer"
          value={filters.customer}
          onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
          className="px-3 py-1.5 rounded-md text-sm bg-white"
          style={{ border: "1px solid var(--color-border)" }}
        >
          <option value="">All customers</option>
          {customerOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          data-testid="filter-status"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-3 py-1.5 rounded-md text-sm bg-white"
          style={{ border: "1px solid var(--color-border)" }}
        >
          <option value="">All statuses</option>
          {["MATCH", "MINOR VARIANCE", "MATERIAL VARIANCE", "REVIEW REQUIRED"].map((s) =>
            <option key={s} value={s}>{s}</option>
          )}
        </select>
        {(filters.customer || filters.status) && (
          <button
            data-testid="filter-clear"
            onClick={() => setFilters({ customer: "", status: "" })}
            className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-slate-50"
            style={{ border: "1px solid var(--color-border)" }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KPICard testId="dk-total" label="Total Shipments" value={summary?.total_shipments ?? 0} tone="info" />
        <KPICard testId="dk-est" label="Total Estimated Duty" value={usd(summary?.total_estimated)} tone="neutral" />
        <KPICard testId="dk-act" label="Total Actual Duty and Fee" value={usd(summary?.total_actual)} tone="neutral" />
        <KPICard testId="dk-var" label={`Net ${plL}`} value={usd(summary?.net_pl)} sub={`vs estimate · ${usd(summary?.net_variance)} CBP delta`} tone={plT} />
      </div>

      {/* Missing Price Backlog widget */}
      <div className="mb-6">
        <MissingPriceBacklog />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-md p-5" style={{ border: "1px solid var(--color-border)" }} data-testid="chart-trend">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#0F2044] mb-3">
            Variance trend (Estimated vs Actual)
          </div>
          {summary?.trend?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={summary.trend}>
                <CartesianGrid stroke="#F1F5F9" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => usd(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="estimated" fill="#3B82F6" name="Estimated" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" fill="#0F2044" name="Actual" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="variance" stroke="#F59E0B" strokeWidth={2} name="Variance" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-gray-500">No data</div>}
        </div>

        <div className="bg-white rounded-md p-5" style={{ border: "1px solid var(--color-border)" }} data-testid="chart-customer-duty">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#0F2044] mb-3">
            Cumulative duty by customer
          </div>
          {summary?.by_customer?.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={summary.by_customer} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid stroke="#F1F5F9" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="customer" width={150} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => usd(v)} />
                <Bar dataKey="actual" fill="#0F2044" radius={[0, 4, 4, 0]}
                  onClick={(d) => setFilters({ ...filters, customer: d.customer })}
                  style={{ cursor: "pointer" }} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-gray-500">No data</div>}
        </div>
      </div>

      <div className="bg-white rounded-md p-5 mb-6" style={{ border: "1px solid var(--color-border)" }} data-testid="chart-customer-variance">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#0F2044] mb-3">
          Net variance by customer
        </div>
        {summary?.by_customer?.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={summary.by_customer} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid stroke="#F1F5F9" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="customer" width={150} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => usd(v)} />
              <Bar dataKey="variance" fill="#F59E0B" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="text-sm text-gray-500">No data</div>}
      </div>

      {/* Shipment history table */}
      <div className="bg-white rounded-md overflow-hidden" style={{ border: "1px solid var(--color-border)" }} data-testid="ship-history">
        <div className="px-4 py-3 border-b font-semibold text-[#0F2044] text-sm uppercase tracking-wider flex items-center justify-between" style={{ borderColor: "var(--color-border)" }}>
          <span>Shipment history</span>
          <span className="text-xs text-gray-500 normal-case">{shipments.total} record(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="dr-table w-full text-sm">
            <thead>
              <tr>
                <th>Session</th>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Entry</th>
                <th>Import</th>
                <th>Incoterm</th>
                <th className="text-right">Est. Duty</th>
                <th className="text-right">Actual</th>
                <th className="text-right">Variance</th>
                <th className="text-right">%</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {shipments.rows.map((r) => {
                const tone = statusTone(r.status);
                const rowCls = tone === "match" ? "row-match"
                  : tone === "warning" ? "row-minor"
                  : tone === "error" ? "row-material"
                  : "row-review";
                return (
                  <tr
                    key={r.reference}
                    className={`${rowCls} cursor-pointer`}
                    onClick={() => navigate(`/shipments/${r.reference}`)}
                    data-testid={`ship-row-${r.reference}`}
                  >
                    <td className="font-mono text-xs">{r.reference}</td>
                    <td className="font-mono text-xs">{r.invoice_number}</td>
                    <td>{r.customer_name}</td>
                    <td className="font-mono text-xs">{r.entry_number}</td>
                    <td className="uppercase-date">{ddmmmyy(r.import_date)}</td>
                    <td>{r.incoterm}</td>
                    <td className="num">{usd(r.total_estimated_duty)}</td>
                    <td className="num">{usd(r.total_actual_duty)}</td>
                    <td className="num">{usd(r.variance_usd)}</td>
                    <td className="num">{pct(r.variance_pct)}</td>
                    <td><StatusBadge status={r.status} /></td>
                  </tr>
                );
              })}
              {!shipments.rows.length && !loading && (
                <tr><td colSpan="11" className="text-center text-gray-500 py-8">No shipments yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
